import { Chip, Divider, IconButton, Typography } from '@mui/joy';
import {
  ArrowRight,
  CaretDown,
  CaretRight,
  Check,
  FunnelSimple,
  Plus,
  TrashSimple,
  X,
} from '@phosphor-icons/react';
import { PopoverClose, PopoverTrigger } from '@radix-ui/react-popover';
import { mergeProps } from '@react-aria/utils';
import clsx from 'clsx';
import { format } from 'date-fns';
import { ReactNode, useState } from 'react';
import { DayPickerSingleProps } from 'react-day-picker';
import { FormProvider, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import { useProcessForm } from './ProcessesTab';
import { RichTextEditorField, WorkflowTaskDialog } from './TaskDialog';

import { Button } from '~/components/ui/button';
import { Calendar } from '~/components/ui/calendar';
import {
  Checkbox,
  CheckboxField,
  CheckboxGroup,
} from '~/components/ui/checkbox';
import {
  DatePickerField,
  DateRangePickerField,
} from '~/components/ui/date-picker';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Label } from '~/components/ui/fieldset';
import { Field } from '~/components/ui/form';
import { InputField, inputFieldClasses } from '~/components/ui/input';
import { Popover, PopoverContent } from '~/components/ui/popover';
import { Option, Select, SelectField } from '~/components/ui/select';
import { useTableBuilder } from '~/components/ui/table-builder';
import { PriorityChip, StatusChip } from '~/features/process-management/Common';
import {
  getPriorityName,
  PRIORITY_MAP,
  PriorityValues,
  TaskStatus,
  taskStatusOptions,
} from '~/features/process-management/configs';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import { SubtaskCount } from '~/pages/customer/settings/final-report/processes/template-editor/route';
import { ProcessService } from '~/services/api/processes/endpoints';
import {
  FilterFormFields,
  filterSchema,
  ProcessWorkflowTask,
} from '~/services/api/processes/types';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { TenantService } from '~/services/api/tenant/endpoints';
import { useForm } from '~/utils/forms';

export const useFilterForm = () => useFormContext<FilterFormFields>();

const schema = z.object({
  id: z.number(),
  name: z.string(),
});

type FormFields = z.infer<typeof schema>;

export function ProcessManagement({
  defaultValues,
}: Readonly<{
  defaultValues: Partial<FormFields>;
}>) {
  const intl = useIntl();
  const { referenceId } = useAppSelector((state) => state.reportData.report);
  const { createToastNotification } = useToastNotificationContext();
  const processForm = useProcessForm();
  const selectedTaskId = processForm.watch('selectedTaskId');
  const workflowId = processForm.watch('workflowId');

  const form = useForm({
    schema,
    defaultValues: {
      ...defaultValues,
    },
  });

  const filterForm = useForm({
    schema: filterSchema,
    defaultValues: {
      status: {
        TODO: false,
        IN_PROGRESS: false,
        UNDER_REVIEW: false,
        COMPLETED: false,
      },
      assignee: [],
      no_assignee: false,
      is_assignee: false,
      priority: {
        CRITICAL: false,
        HIGH: false,
        MEDIUM: false,
        LOW: false,
      },
      custom_date_value: {
        from: null,
        to: null,
      },
      custom_date: false,
      overdue: false,
      no_due_date: false,
    },
  });

  const deleteWorkflow = useWrappedMutation(
    ProcessService.endpoints.deleteProcessWorkflow.useMutation(),
  );

  const deleteHandler = form.handleSubmit(async (data) => {
    await deleteWorkflow.mutateAsync(
      {
        workflow_id: data.id,
        params: {
          reference_id: referenceId,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Process Workflow deleted successfully',
            }),
            'success',
          );
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  const [updateTitle, setUpdateTitle] = useState(false);

  const openUpdateTitle = () => setUpdateTitle(true);
  const closeUpdateTitle = () => setUpdateTitle(false);

  const updateWorkflowTitle = useWrappedMutation(
    ProcessService.endpoints.updateReportWorkflow.useMutation(),
  );
  const updateTitleHandler = form.handleSubmit(async (data) => {
    await updateWorkflowTitle.mutateAsync(
      {
        id: data.id,
        payload: {
          name: data.name,
        },
        params: {
          reference_id: referenceId,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Process Workflow title updated successfully',
            }),
            'success',
          );
          closeUpdateTitle();
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  return (
    <>
      {/* <pre>{JSON.stringify(filterForm.watch(), null, 2)}</pre> */}
      {selectedTaskId && workflowId && (
        <WorkflowTaskDialog
          key={`${workflowId}-${selectedTaskId}`}
          open={!isNaN(selectedTaskId)}
          workflowId={workflowId}
          taskId={selectedTaskId}
        />
      )}
      <FormProvider {...filterForm}>
        <form onSubmit={updateTitleHandler}>
          <div className='flex flex-col h-full overflow-auto gap-2'>
            <div className='flex flex-row justify-between items-center'>
              <div className='flex flex-row items-center'>
                <div
                  className='flex gap-2 justify-center items-center'
                  onFocus={openUpdateTitle}
                >
                  <InputField
                    control={form.control}
                    field='name'
                    size='lg'
                    variant='plain'
                    className='!text-2xl font-semibold'
                  />
                  {(updateTitle || updateWorkflowTitle.state.isLoading) && (
                    <>
                      <IconButton
                        color='success'
                        variant='outlined'
                        onClick={updateTitleHandler}
                        loading={updateWorkflowTitle.state.isLoading}
                      >
                        <Check />
                      </IconButton>
                      <IconButton
                        color='danger'
                        variant='outlined'
                        onClick={closeUpdateTitle}
                        disabled={updateWorkflowTitle.state.isLoading}
                      >
                        <X />
                      </IconButton>
                    </>
                  )}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <IconButton>
                      <CaretDown />
                    </IconButton>
                  </PopoverTrigger>
                  <PopoverContent align='start'>
                    <div className='flex flex-col gap-2'>
                      <Button
                        variant='plain'
                        color='neutral'
                        className='!justify-start'
                        startDecorator={<TrashSimple />}
                        onClick={deleteHandler}
                        loading={deleteWorkflow.state.isLoading}
                      >
                        <FormattedMessage defaultMessage='Delete Template' />
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className='flex flex-row gap-2'>
                <FilterPopover />
                <AddTaskDialog defaultValues={{ templateId: form.watch('id') }}>
                  <Button
                    variant='outlined'
                    color='neutral'
                    size='lg'
                    startDecorator={<Plus />}
                  >
                    <FormattedMessage defaultMessage='Add Task' />
                  </Button>
                </AddTaskDialog>
              </div>
            </div>
            <WorkflowTable
              workflow_id={defaultValues.id}
              onTaskSelected={(taskId) =>
                processForm.setValue('selectedTaskId', taskId)
              }
            />
          </div>
        </form>
      </FormProvider>
    </>
  );
}

const FilterPopover = () => {
  const slug = useTenantSlug();
  const userListQuery = TenantService.endpoints.tenantUsers.useQuery({
    tenantSlug: slug,
    params: {
      all: 'true',
    },
  });

  const form = useFilterForm();

  const selectAsAssignee = (userId: number) => {
    form.setValue('no_assignee', false);

    const currentAssignees = form.watch('assignee');
    if (currentAssignees.includes(userId)) {
      form.setValue(
        'assignee',
        currentAssignees.filter((assignee) => assignee !== userId),
      );
    } else {
      form.setValue('assignee', [...currentAssignees, userId]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='plain'
          color='neutral'
          size='lg'
          className='!justify-start'
          startDecorator={<FunnelSimple />}
        >
          <FormattedMessage defaultMessage='Filter' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start'>
        <div className='flex flex-col gap-2'>
          {/* TASK STATUS */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='plain'
                color='neutral'
                className='!justify-start'
              >
                <FormattedMessage defaultMessage='Task Status' />
              </Button>
            </PopoverTrigger>
            <PopoverContent side='left' sideOffset={20} align='start'>
              <div className='flex flex-col gap-2'>
                <PopoverClose asChild>
                  <Button
                    variant='plain'
                    color='neutral'
                    className='!justify-start'
                    endDecorator={<ArrowRight />}
                  >
                    <FormattedMessage defaultMessage='Task Status' />
                  </Button>
                </PopoverClose>
                <CheckboxGroup className='flex flex-col'>
                  <Checkbox
                    checked={form.watch('status.TODO')}
                    onChange={(e) => {
                      form.setValue('status', {
                        ...form.watch('status'),
                        TODO: e.target.checked,
                      });
                    }}
                    color='secondary'
                    size='sm'
                    label={<StatusChip status={TaskStatus.TODO} />}
                  />
                  <Checkbox
                    checked={form.watch('status.IN_PROGRESS')}
                    onChange={(e) => {
                      form.setValue('status', {
                        ...form.watch('status'),
                        IN_PROGRESS: e.target.checked,
                      });
                    }}
                    color='secondary'
                    size='sm'
                    label={<StatusChip status={TaskStatus.IN_PROGRESS} />}
                  />
                  <Checkbox
                    checked={form.watch('status.UNDER_REVIEW')}
                    onChange={(e) => {
                      form.setValue('status', {
                        ...form.watch('status'),
                        UNDER_REVIEW: e.target.checked,
                      });
                    }}
                    color='secondary'
                    size='sm'
                    label={<StatusChip status={TaskStatus.UNDER_REVIEW} />}
                  />
                  <Checkbox
                    checked={form.watch('status.COMPLETED')}
                    onChange={(e) => {
                      form.setValue('status', {
                        ...form.watch('status'),
                        COMPLETED: e.target.checked,
                      });
                    }}
                    color='secondary'
                    size='sm'
                    label={<StatusChip status={TaskStatus.COMPLETED} />}
                  />
                </CheckboxGroup>
              </div>
            </PopoverContent>
          </Popover>
          {/* ASIGNEE */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='plain'
                color='neutral'
                className='!justify-start'
              >
                <FormattedMessage defaultMessage='Assignee' />
              </Button>
            </PopoverTrigger>
            <PopoverContent side='left' sideOffset={20} align='start'>
              <div className='flex flex-col gap-2'>
                <PopoverClose asChild>
                  <Button
                    variant='plain'
                    color='neutral'
                    className='!justify-start'
                    endDecorator={<ArrowRight />}
                  >
                    <FormattedMessage defaultMessage='Assignee' />
                  </Button>
                </PopoverClose>
                <CheckboxGroup className='max-h-52 overflow-y-auto overflow-x-hidden'>
                  <CheckboxField
                    control={form.control}
                    field='no_assignee'
                    color='secondary'
                    size='sm'
                    label='No Assignee'
                    onChange={(e) => {
                      if (e.target.checked) {
                        form.setValue('assignee', []);
                        form.setValue('is_assignee', false);
                      }
                    }}
                  />
                  <CheckboxField
                    control={form.control}
                    field='is_assignee'
                    color='secondary'
                    size='sm'
                    label='Assigned to me'
                    onChange={() => {
                      form.setValue('no_assignee', false);
                    }}
                  />
                  <QueryResolver query={userListQuery}>
                    {(data) => (
                      <div className='flex flex-col gap-2'>
                        {data.account_settings_user_list.map((user) => (
                          <Checkbox
                            key={user.id}
                            color='secondary'
                            size='sm'
                            label={user.full_name}
                            checked={form.watch('assignee').includes(user.id)}
                            onChange={() => selectAsAssignee(user.id)}
                          />
                        ))}
                      </div>
                    )}
                  </QueryResolver>
                </CheckboxGroup>
              </div>
            </PopoverContent>
          </Popover>
          {/* PRIORITY */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='plain'
                color='neutral'
                className='!justify-start'
              >
                <FormattedMessage defaultMessage='Priority' />
              </Button>
            </PopoverTrigger>
            <PopoverContent side='left' sideOffset={20} align='start'>
              <div className='flex flex-col gap-2'>
                <PopoverClose asChild>
                  <Button
                    variant='plain'
                    color='neutral'
                    className='!justify-start'
                    endDecorator={<ArrowRight />}
                  >
                    <FormattedMessage defaultMessage='Priority' />
                  </Button>
                </PopoverClose>
                <CheckboxGroup className='flex flex-col '>
                  <Checkbox
                    checked={form.watch('priority.CRITICAL')}
                    onChange={(e) => {
                      form.setValue('priority', {
                        ...form.watch('priority'),
                        CRITICAL: e.target.checked,
                      });
                    }}
                    color='secondary'
                    size='sm'
                    label={<PriorityChip priority={PriorityValues.CRITICAL} />}
                  />
                  <Checkbox
                    checked={form.watch('priority.HIGH')}
                    onChange={(e) => {
                      form.setValue('priority', {
                        ...form.watch('priority'),
                        HIGH: e.target.checked,
                      });
                    }}
                    color='secondary'
                    size='sm'
                    label={<PriorityChip priority={PriorityValues.HIGH} />}
                  />
                  <Checkbox
                    checked={form.watch('priority.MEDIUM')}
                    onChange={(e) => {
                      form.setValue('priority', {
                        ...form.watch('priority'),
                        MEDIUM: e.target.checked,
                      });
                    }}
                    color='secondary'
                    size='sm'
                    label={<PriorityChip priority={PriorityValues.MEDIUM} />}
                  />
                  <Checkbox
                    checked={form.watch('priority.LOW')}
                    onChange={(e) => {
                      form.setValue('priority', {
                        ...form.watch('priority'),
                        LOW: e.target.checked,
                      });
                    }}
                    color='secondary'
                    size='sm'
                    label={<PriorityChip priority={PriorityValues.LOW} />}
                  />
                </CheckboxGroup>
              </div>
            </PopoverContent>
          </Popover>

          {/* DUE DATE */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='plain'
                color='neutral'
                className='!justify-start'
              >
                <FormattedMessage defaultMessage='Due date' />
              </Button>
            </PopoverTrigger>
            <PopoverContent side='left' sideOffset={20} align='center'>
              <div className='flex flex-col gap-2'>
                <PopoverClose asChild>
                  <Button
                    variant='plain'
                    color='neutral'
                    className='!justify-start'
                    endDecorator={<ArrowRight />}
                  >
                    <FormattedMessage defaultMessage='Due date' />
                  </Button>
                </PopoverClose>
                <CheckboxGroup className='flex flex-col'>
                  <CheckboxField
                    control={form.control}
                    field='custom_date'
                    color='secondary'
                    size='sm'
                    label={<FormattedMessage defaultMessage='Custom' />}
                    onChange={() => {
                      form.setValue('overdue', false);
                      form.setValue('no_due_date', false);
                    }}
                  />
                  {form.watch('custom_date') && (
                    <DateRangePickerField
                      control={form.control}
                      field='custom_date_value'
                    />
                  )}
                  <CheckboxField
                    control={form.control}
                    field='overdue'
                    color='secondary'
                    size='sm'
                    label={<FormattedMessage defaultMessage='Overdate' />}
                    onChange={() => {
                      form.setValue('custom_date', false);
                      form.setValue('no_due_date', false);
                    }}
                  />
                  <CheckboxField
                    control={form.control}
                    field='no_due_date'
                    color='secondary'
                    size='sm'
                    label={<FormattedMessage defaultMessage='No due date' />}
                    onChange={() => {
                      form.setValue('custom_date', false);
                      form.setValue('overdue', false);
                    }}
                  />
                </CheckboxGroup>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TABLEHEADERINDICATOR = -9;

const initialState = [
  // Add on the task status id array to render header titles at the start of the table
  { id: TABLEHEADERINDICATOR, isOpen: true },
  ...Object.values(TaskStatus)
    .filter((value) => typeof value === 'number')
    .map((id) => ({
      id: id as number,
      isOpen: true,
    })),
];

const WorkflowTable = ({
  workflow_id,
  onTaskSelected,
}: {
  workflow_id?: number;
  onTaskSelected: (taskId: number) => void;
}) => {
  const intl = useIntl();
  const [taskStatuses, setTaskStatuses] = useState(initialState);
  const filterForm = useFilterForm();

  const { Table, Column } = useTableBuilder<ProcessWorkflowTask>();
  const filters = filterForm.watch();

  const subtasksQuery = ProcessService.endpoints.listWorkflowtasks.useQuery(
    {
      workflow_id: workflow_id ?? 0,
      params: filters,
    },
    { skip: !workflow_id },
  );

  const toggleTaskStatusState = (statusId: number) => {
    setTaskStatuses((prevStatuses) =>
      prevStatuses.map((status) =>
        status.id === statusId ? { ...status, isOpen: !status.isOpen } : status,
      ),
    );
  };

  return (
    <QueryResolver query={subtasksQuery} strategy='stale-while-revalidate'>
      {(data) => {
        return taskStatuses.map(({ id: taskStatusId, isOpen }) => (
          <>
            {taskStatusId !== TABLEHEADERINDICATOR && (
              <div className='flex flex-row items-center'>
                <IconButton onClick={() => toggleTaskStatusState(taskStatusId)}>
                  {isOpen ? (
                    <CaretDown weight='fill' />
                  ) : (
                    <CaretRight weight='fill' />
                  )}
                </IconButton>
                <div>
                  <StatusChip
                    status={taskStatusId}
                    size='md'
                    count={
                      data.filter((item) => item.status === taskStatusId).length
                    }
                  />
                </div>
              </div>
            )}
            {isOpen && (
              <Table
                data={[...data]
                  .filter((item) => item.status === taskStatusId)
                  .sort((a, b) =>
                    a.control_id
                      .toLowerCase()
                      .localeCompare(b.control_id.toLowerCase()),
                  )}
                hoverRow
                key={taskStatusId}
                emptyMessage={
                  taskStatusId !== TABLEHEADERINDICATOR ? (
                    <Typography>
                      <FormattedMessage defaultMessage='No Item' />
                    </Typography>
                  ) : (
                    <></>
                  )
                }
                noHeader={taskStatusId !== TABLEHEADERINDICATOR}
                headerOnly={taskStatusId === TABLEHEADERINDICATOR}
              >
                <Column
                  style={{
                    width: '10%',
                  }}
                  header={
                    taskStatusId === TABLEHEADERINDICATOR &&
                    intl.formatMessage({ defaultMessage: 'Control ID' })
                  }
                >
                  {(row) => row.control_id}
                </Column>
                <Column
                  style={{
                    width: '30%',
                  }}
                  header={
                    taskStatusId === TABLEHEADERINDICATOR &&
                    intl.formatMessage({ defaultMessage: 'Task Name' })
                  }
                >
                  {(row) => (
                    <Button
                      variant='plain'
                      color='neutral'
                      onClick={() => {
                        onTaskSelected(row.id);
                      }}
                      endDecorator={
                        <SubtaskCount count={row?.children?.length} />
                      }
                    >
                      {row.name}
                    </Button>
                  )}
                </Column>
                <Column
                  style={{
                    width: '17%',
                  }}
                  header={
                    taskStatusId === TABLEHEADERINDICATOR &&
                    intl.formatMessage({ defaultMessage: 'Assignee' })
                  }
                >
                  {(row) => (
                    <SelectAssignee
                      assignee={row.assignee_details?.id}
                      taskId={row.id}
                      workflow_id={workflow_id}
                    />
                  )}
                </Column>
                <Column
                  style={{
                    width: '10%',
                  }}
                  header={
                    taskStatusId === TABLEHEADERINDICATOR &&
                    intl.formatMessage({ defaultMessage: 'Due Date' })
                  }
                >
                  {(row) => (
                    <SelectDueDate
                      dueDate={row.due_date}
                      taskId={row.id}
                      workflow_id={workflow_id}
                    />
                  )}
                </Column>
                <Column
                  style={{
                    width: '13%',
                  }}
                  header={
                    taskStatusId === TABLEHEADERINDICATOR &&
                    intl.formatMessage({ defaultMessage: 'Priority' })
                  }
                >
                  {(row) => (
                    <SelectPriority
                      priority={row.priority}
                      taskId={row.id}
                      workflow_id={workflow_id}
                    />
                  )}
                </Column>
                <Column
                  style={{
                    width: '20%',
                  }}
                  header={
                    taskStatusId === TABLEHEADERINDICATOR &&
                    intl.formatMessage({ defaultMessage: 'Task Status' })
                  }
                >
                  {(row) => (
                    <SelectStatus
                      status={row.status}
                      taskId={row.id}
                      workflow_id={workflow_id}
                    />
                  )}
                </Column>
              </Table>
            )}
          </>
        ));
      }}
    </QueryResolver>
  );
};

export function SelectStatus({
  status,
  taskId,
  workflow_id,
  mountOptionsToRoot = false,
}: Readonly<{
  status: number;
  taskId: number;
  workflow_id?: number;
  mountOptionsToRoot?: boolean;
}>) {
  const intl = useIntl();
  const filterForm = useFilterForm();
  const filters = filterForm.watch();

  const updateTask = useWrappedMutation(
    ProcessService.endpoints.updateWorkflowTask.useMutation(),
  );

  const updateTastStatus = async (statusId: number, id: number) => {
    await updateTask.mutateAsync({
      subtask_id: id,
      workflow_id: workflow_id,
      payload: {
        status: statusId,
      },
      params: filters,
    });
  };

  return (
    <Select
      value={status}
      variant='plain'
      className='!bg-transparent'
      placeholder={intl.formatMessage({
        defaultMessage: 'Select Status',
      })}
      renderValue={() =>
        status === null ? '--' : <StatusChip status={status} />
      }
      onChange={(_, value) => {
        updateTastStatus(Number(value), taskId);
      }}
      mountOptionsToRoot={mountOptionsToRoot}
    >
      {taskStatusOptions.map((item) => (
        <Option key={item.label} value={item.value}>
          <StatusChip status={item.value} />
        </Option>
      ))}
    </Select>
  );
}

export function SelectPriority({
  priority,
  taskId,
  workflow_id,
  mountOptionsToRoot = false,
}: Readonly<{
  priority: number;
  taskId: number;
  workflow_id?: number;
  mountOptionsToRoot?: boolean;
}>) {
  const intl = useIntl();
  const filterForm = useFilterForm();
  const filters = filterForm.watch();

  const updateTask = useWrappedMutation(
    ProcessService.endpoints.updateWorkflowTask.useMutation(),
  );

  const updateTastPriority = async (priorityId: number, id: number) => {
    await updateTask.mutateAsync({
      subtask_id: id,
      workflow_id: workflow_id,
      payload: {
        priority: priorityId,
      },
      params: filters,
    });
  };
  return (
    <Select
      value={priority}
      variant='plain'
      className='!bg-transparent'
      placeholder={intl.formatMessage({
        defaultMessage: 'Set Priority',
      })}
      renderValue={() =>
        getPriorityName(priority) === undefined ? (
          '--'
        ) : (
          <PriorityChip priority={getPriorityName(priority)} />
        )
      }
      onChange={(_, value) => {
        updateTastPriority(Number(value), taskId);
      }}
      mountOptionsToRoot={mountOptionsToRoot}
    >
      {PRIORITY_MAP.map((item) => (
        <Option key={item.priority_code} value={item.priority_code}>
          <PriorityChip priority={getPriorityName(item.priority_code)} />
        </Option>
      ))}
    </Select>
  );
}

export function SelectAssignee({
  assignee,
  taskId,
  mountOptionsToRoot = false,
  workflow_id,
}: Readonly<{
  assignee: number;
  taskId: number;
  mountOptionsToRoot?: boolean;
  workflow_id?: number;
}>) {
  const intl = useIntl();
  const filterForm = useFilterForm();
  const filters = filterForm.watch();
  const slug = useTenantSlug();
  const userListQuery = TenantService.endpoints.tenantUsers.useQuery({
    tenantSlug: slug,
    params: {
      all: 'true',
    },
  });
  const updateTask = useWrappedMutation(
    ProcessService.endpoints.updateWorkflowTask.useMutation(),
  );

  const updateTastAssignee = async (assigneeId: number, id: number) => {
    await updateTask.mutateAsync({
      subtask_id: id,
      workflow_id: workflow_id,
      payload: {
        assignee: assigneeId,
      },
      params: filters,
    });
  };

  return (
    <QueryResolver query={userListQuery} loadingElement={<></>}>
      {(data) => (
        <Select
          value={assignee}
          variant='plain'
          className='!bg-transparent'
          placeholder={intl.formatMessage({
            defaultMessage: 'Assign',
          })}
          onChange={(_, value) => {
            updateTastAssignee(Number(value), taskId);
          }}
          mountOptionsToRoot={mountOptionsToRoot}
        >
          {data.account_settings_user_list.map((user) => (
            <Option key={user.id} value={user.id}>
              {user.full_name}
            </Option>
          ))}
        </Select>
      )}
    </QueryResolver>
  );
}

export function SelectDueDate({
  dueDate,
  taskId,
  workflow_id,
}: Readonly<{
  dueDate: Date | null;
  taskId: number;
  workflow_id?: number;
}>) {
  const [open, setOpen] = useState(false);
  const filterForm = useFilterForm();
  const filters = filterForm.watch();

  const updateTask = useWrappedMutation(
    ProcessService.endpoints.updateWorkflowTask.useMutation(),
  );

  const updateTaskDueDate = async (due_date: Date, id: number) => {
    await updateTask.mutateAsync({
      subtask_id: id,
      workflow_id: workflow_id,
      payload: {
        due_date: due_date,
      },
      params: filters,
    });
  };
  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button variant='plain' color='neutral' className='!bg-transparent'>
          {dueDate ? (
            <Chip variant='outlined' color='neutral'>
              {dueDate ? format(dueDate, 'MMM-dd') : '--'}
            </Chip>
          ) : (
            <FormattedMessage defaultMessage='Set' />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto z-10 relative' bleed>
        <Calendar
          {...mergeProps({
            onSelect: () => {
              setOpen(false);
            },
          } as Partial<DayPickerSingleProps>)}
          mode='single'
          onDayClick={(newDate) => {
            updateTaskDueDate(newDate, taskId);
          }}
          showOutsideDays={true}
          disabled={(date) => {
            const today = new Date();
            today.setDate(today.getDate() - 1);
            return date < today;
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

const addTaskSchema = z.object({
  templateId: z.number(),
  name: z.string().min(1).max(255),
  description: z.string().min(1).max(2000),
  assignee: z.number().optional(),
  due_date: z.date().optional(),
  priority: z.number(),
});

type AddTaskSchema = z.infer<typeof addTaskSchema>;

const AddTaskDialog = ({
  children,
  defaultValues,
}: {
  children: ReactNode;
  defaultValues: Partial<AddTaskSchema>;
}) => {
  const intl = useIntl();
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const [open, setOpen] = useState(false);
  const setOpenChange = () => setOpen((prev) => !prev);

  const userListQuery = TenantService.endpoints.tenantUsers.useQuery({
    tenantSlug: slug,
    params: {
      all: 'true',
    },
  });

  const form = useForm({
    schema: addTaskSchema,
    defaultValues: {
      ...defaultValues,
      name: '',
      description: '',
    },
  });

  const addTask = useWrappedMutation(
    ProcessService.endpoints.addWorkflowTask.useMutation(),
  );

  const handleSubmit = form.handleSubmit(async (data) => {
    await addTask.mutateAsync(
      {
        payload: {
          name: data.name,
          description: data.description,
          assignee: data.assignee,
          due_date: data.due_date,
          priority: data.priority,
          template: data.templateId,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Task added successfully' }),
            'success',
          );
          form.reset();
          setOpenChange();
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });
  return (
    <Dialog open={open} onOpenChange={setOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent size='4xl'>
        <div className='flex flex-row justify-between items-center'>
          <DialogTitle>
            <FormattedMessage defaultMessage='New Task' />
          </DialogTitle>
          <DialogClose asChild>
            <IconButton>
              <X />
            </IconButton>
          </DialogClose>
        </div>
        <form noValidate onSubmit={handleSubmit}>
          <div className='flex flex-col gap-4'>
            <InputField
              control={form.control}
              field='name'
              size='lg'
              variant='plain'
              className='!text-2xl !font-semibold'
              placeholder='Task name'
            />
            <Field size='lg' control={form.control} field='description'>
              <div className={clsx(inputFieldClasses)}>
                <div
                  data-slot='label'
                  className='flex items-center justify-between'
                >
                  <Label required={true}>
                    <FormattedMessage defaultMessage='Description' />
                  </Label>
                </div>
                <RichTextEditorField />
              </div>
            </Field>
            <div className='flex gap-2 items-baseline'>
              <StatusChip status={TaskStatus.TODO} size='lg' />
              <SelectField
                control={form.control}
                field='priority'
                placeholder={intl.formatMessage({
                  defaultMessage: 'Set Priority',
                })}
                mountOptionsToRoot={true}
              >
                {PRIORITY_MAP.map((item) => (
                  <Option key={item.priority_code} value={item.priority_code}>
                    <PriorityChip
                      priority={getPriorityName(item.priority_code)}
                    />
                  </Option>
                ))}
              </SelectField>
              <SelectField
                control={form.control}
                field='assignee'
                placeholder={intl.formatMessage({ defaultMessage: 'Assignee' })}
                mountOptionsToRoot={true}
              >
                <QueryResolver query={userListQuery} loadingElement={<></>}>
                  {(data) => (
                    <div>
                      {data.account_settings_user_list.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {user.full_name}
                        </Option>
                      ))}
                    </div>
                  )}
                </QueryResolver>
              </SelectField>
              <DatePickerField
                control={form.control}
                field='due_date'
                placeholder={intl.formatMessage({
                  defaultMessage: 'Select Due Date',
                })}
              />
            </div>
            <Divider />
            <div className='flex flex-col items-end w-full'>
              <Button
                className='w-fit'
                color='secondary'
                type='submit'
                loading={addTask.state.isLoading}
              >
                <FormattedMessage defaultMessage='Create Task' />
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
